import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, MapPin, Users, X, ChevronRight } from 'lucide-react';
import Footer from '../components/common/Footer';
import physicsCircles from '../data/physicsCircles';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/* ─── Circle card ─── */
const CircleCard = ({ circle, index, onSelect }) => {
  const cityName = circle.location.split(',')[0].trim();
  const countryName = circle.location.split(',').slice(1).join(',').trim();

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 36 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      whileTap={{ scale: 0.97 }}
      onClick={() => onSelect(circle)}
      className="group flex flex-col items-center rounded-2xl border border-gray-200/80 bg-white px-6 pb-6 pt-8 text-center shadow-sm transition-all duration-500 hover:shadow-[0_24px_64px_rgba(0,51,160,0.12)] hover:border-[#0033A0]/20"
    >
      {/* Circular photo */}
      <div className="mb-5 h-36 w-36 overflow-hidden rounded-full border-4 border-white bg-gradient-to-br from-[#f5f5f7] to-[#e8e8ed] shadow-lg transition-shadow duration-500 group-hover:shadow-[0_12px_40px_rgba(0,51,160,0.18)]">
        {circle.leader.photo && (
          <img
            src={circle.leader.photo}
            alt={circle.leader.name}
            className="h-full w-full object-cover object-[center_25%] grayscale transition-[filter,transform] duration-700 group-hover:grayscale-0 group-hover:scale-110"
          />
        )}
      </div>

      {/* City & country */}
      <h3 className="text-2xl font-bold tracking-tight text-[#1d1d1f] transition-colors duration-300 group-hover:text-[#0033A0]">
        {cityName}
      </h3>
      {countryName && (
        <p className="mt-1 flex items-center justify-center gap-1 text-sm font-medium text-[#86868b]">
          <MapPin size={12} />
          {countryName}
        </p>
      )}

      {/* Leader name */}
      <p className="mt-3 text-sm font-semibold text-[#1d1d1f]">{circle.leader.name}</p>
      <p className="text-xs text-[#86868b]">Circle leader</p>

      {/* Student count */}
      <div className="mt-3 flex items-center gap-2">
        <div className="flex -space-x-1.5">
          {circle.students.slice(0, 3).map((name, i) => (
            <div
              key={i}
              className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-[#0033A0] text-[8px] font-bold text-white"
            >
              {name.charAt(0)}
            </div>
          ))}
        </div>
        <span className="text-xs text-[#86868b]">{circle.students.length} members</span>
        <ChevronRight size={14} className="text-[#86868b] transition-transform duration-300 group-hover:translate-x-1" />
      </div>
    </motion.button>
  );
};

/* ─── Full-screen modal ─── */
const CircleModal = ({ circle, onClose }) => {
  if (!circle) return null;
  const cityName = circle.location.split(',')[0].trim();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}
        transition={{ type: 'spring', damping: 26, stiffness: 300 }}
        className="relative w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-8 shadow-2xl max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-2 text-[#1d1d1f]/60 hover:bg-gray-100 transition-colors"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="mb-6">
          <p className="text-3xl font-bold tracking-tight text-[#1d1d1f]">{cityName}</p>
          <p className="mt-1 text-sm text-[#86868b]">{circle.location}</p>
          <p className="mt-2 text-base font-semibold text-[#0033A0]">{circle.name}</p>
        </div>

        {/* Leader */}
        <div className="mb-6 flex items-center gap-4 rounded-2xl bg-[#f5f5f7] p-4">
          {circle.leader.photo && (
            <img
              src={circle.leader.photo}
              alt={circle.leader.name}
              className="h-14 w-14 rounded-full object-cover ring-2 ring-white shadow"
            />
          )}
          <div>
            <p className="font-semibold text-[#1d1d1f]">{circle.leader.name}</p>
            <p className="text-sm text-[#86868b]">Circle leader</p>
            {circle.leader.email && (
              <a
                href={`mailto:${circle.leader.email}`}
                className="mt-1 inline-flex items-center gap-1 text-sm text-[#0033A0] hover:underline"
              >
                <Mail size={13} /> {circle.leader.email}
              </a>
            )}
          </div>
        </div>

        {/* Students */}
        <div>
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-[#86868b]">
            Students ({circle.students.length})
          </p>
          <div className="grid grid-cols-2 gap-2">
            {circle.students.map((name, i) => (
              <div
                key={i}
                className="rounded-lg bg-[#f5f5f7] px-3 py-2 text-sm text-[#1d1d1f]"
              >
                {name}
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ─── Page ─── */
const PhysicsCircles = () => {
  const [selectedCircle, setSelectedCircle] = useState(null);
  const heroRef = useRef(null);
  const gridRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.hero-line', {
        y: 60,
        opacity: 0,
        duration: 1,
        stagger: 0.12,
        ease: 'power3.out',
      });

      gsap.from('.how-card', {
        y: 40,
        opacity: 0,
        duration: 0.8,
        stagger: 0.1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.how-section',
          start: 'top 80%',
        },
      });
    });
    return () => ctx.revert();
  }, []);

  const totalStudents = physicsCircles.reduce((sum, c) => sum + c.students.length, 0);
  const countries = [...new Set(physicsCircles.map((c) => c.location.split(',').pop().trim()))];

  return (
    <div className="min-h-screen bg-[#f5f5f7] text-[#1d1d1f] font-sans selection:bg-black selection:text-white">

      {/* ═══ HERO ═══ */}
      <section
        ref={heroRef}
        className="relative overflow-hidden bg-gradient-to-b from-[#f5f5f7] to-white"
      >
        <div className="mx-auto max-w-[90rem] flex-col px-6 py-20 pt-32">
          <div className="hero-line mb-8">
            <span className="inline-flex items-center gap-2 rounded-full border border-black/[0.08] px-3.5 py-1.5 font-mono text-xs uppercase tracking-[0.28em] text-[#86868b]">
              <span className="h-2 w-2 animate-pulse rounded-full bg-[#0033A0]" />
              Community Outreach
            </span>
          </div>

          <h1 className="hero-line max-w-5xl text-[clamp(2.8rem,8vw,6.5rem)] font-bold leading-[0.88] tracking-tighter text-[#1d1d1f]">
            Physics Circles
          </h1>
          <p className="hero-line mt-6 max-w-2xl text-xl font-light leading-relaxed text-[#6e6e73] md:text-2xl">
            Small, student-led groups bringing particle physics to classrooms around the world, using the Virtual Beamline as their shared laboratory.
          </p>

          {/* Stats row */}
          <div className="hero-line mt-14 flex flex-wrap gap-12 border-t border-gray-200 pt-8">
            {[
              { value: physicsCircles.length, label: 'Active Circles' },
              { value: totalStudents, label: 'Students' },
              { value: countries.length, label: 'Countries' },
            ].map((s) => (
              <div key={s.label}>
                <p className="text-4xl font-bold tracking-tight text-[#1d1d1f]">{s.value}</p>
                <p className="mt-1 text-sm text-[#86868b]">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section className="how-section bg-white px-6 py-24 md:py-32">
        <div className="mx-auto max-w-6xl">
          <p className="mb-3 font-mono text-xs uppercase tracking-widest text-[#86868b]">How it works</p>
          <h2 className="mb-16 text-3xl font-bold tracking-tight text-[#1d1d1f] md:text-4xl">
            Three steps to a circle.
          </h2>

          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                step: '01',
                title: 'A leader starts',
                body: 'A member of the Z-Scanner collaboration anchors a circle in their city and organises regular sessions.',
              },
              {
                step: '02',
                title: 'Students gather',
                body: 'A small group joins and meets weekly to run experiments, discuss results, and build intuition together.',
              },
              {
                step: '03',
                title: 'Physics spreads',
                body: 'Each circle becomes a local hub where particle physics is no longer abstract. Students teach each other.',
              },
            ].map((item) => (
              <div
                key={item.step}
                className="how-card rounded-2xl border border-gray-100 bg-[#f5f5f7] p-8"
              >
                <span className="mb-4 inline-block font-mono text-sm font-semibold text-[#0033A0]">
                  {item.step}
                </span>
                <h3 className="mb-2 text-xl font-bold tracking-tight text-[#1d1d1f]">{item.title}</h3>
                <p className="text-base leading-relaxed text-[#6e6e73]">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CIRCLE GRID ═══ */}
      <section ref={gridRef} className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-6xl">
          <p className="mb-3 font-mono text-xs uppercase tracking-widest text-[#86868b]">The network</p>
          <h2 className="mb-4 text-3xl font-bold tracking-tight text-[#1d1d1f] md:text-4xl">
            Active circles.
          </h2>
          <p className="mb-16 max-w-xl text-base text-[#6e6e73]">
            Tap a card to learn more about each circle, its leader, and its students.
          </p>

          <div className="grid gap-x-6 gap-y-12 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {physicsCircles.map((circle, i) => (
              <CircleCard
                key={circle.id}
                circle={circle}
                index={i}
                onSelect={setSelectedCircle}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="bg-white px-6 py-24 text-center md:py-32">
        <div className="mx-auto max-w-2xl">
          <h2 className="text-3xl font-bold tracking-tight text-[#1d1d1f] md:text-4xl">
            Start a circle in your city.
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-base text-[#6e6e73]">
            All you need is curiosity and a few friends. We provide the simulator, the curriculum, and the support.
          </p>
          <a
            href="mailto:join@virtualbeamline.org?subject=Starting a Physics Circle"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#0033A0] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#0033A0]/20 transition-all hover:brightness-110 active:scale-[0.97]"
          >
            <Mail size={16} /> Get in touch
          </a>
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {selectedCircle && (
          <CircleModal
            circle={selectedCircle}
            onClose={() => setSelectedCircle(null)}
          />
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default PhysicsCircles;